import React, { useState, useEffect } from "react";
import WhiteLogo from "../../assets/images/white-logo.svg";
import Logo from "../../assets/images/logo.svg";
import ImgPhone from "../../assets/images/login/mobiles.svg";
import { Swiper, SwiperSlide } from 'swiper/react';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
// import required modules
import { Pagination } from 'swiper/modules';
import Loader from "../../components/common/Loader";
import { useNavigate, useLocation, Link } from "react-router-dom";
import authService from "../../Services/authService";
import { containsExecutableMarkup, deepSanitizePayload } from "../../utils/inputValidation";

const Login = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const [loader, setLoader] = useState(false);
    const [formData, setFormData] = useState({
        email: "",
        password: ""
    });
    const [errors, setErrors] = useState({});
    const [rememberMe, setRememberMe] = useState(false);

    // Account lockout & brute-force protection
    const [lockoutSeconds, setLockoutSeconds] = useState(0);
    const [isAccountLocked, setIsAccountLocked] = useState(false);

    // Display session termination reason if redirected
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const reason = params.get("reason");
        if (reason) {
            const lowerReason = reason.toLowerCase();
            if (reason === "session_terminated") {
                setErrors({
                    general: "Your session was terminated from another browser tab or window."
                });
            } else if (lowerReason.includes("revoked") || lowerReason.includes("device")) {
                setErrors({
                    general: "Your session has expired or was revoked. Please log in again."
                });
            } else if (lowerReason.includes("security")) {
                setErrors({
                    general: "Session terminated due to a security violation. Please log in again."
                });
            } else if (lowerReason.includes("expired")) {
                setErrors({
                    general: "Your session has expired. Please log in again."
                });
            } else if (reason !== "user_action" && reason !== "user_logout") {
                setErrors({
                    general: decodeURIComponent(reason)
                });
            }
        }
    }, [location.search]);

    // Countdown timer for lockout period
    useEffect(() => {
        let timer;
        if (lockoutSeconds > 0) {
            timer = setInterval(() => {
                setLockoutSeconds((prev) => {
                    if (prev <= 1) {
                        setIsAccountLocked(false);
                        setErrors((prevErr) => {
                            const updated = { ...prevErr };
                            delete updated.accountLocked;
                            return updated;
                        });
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [lockoutSeconds]);

    // Handle input changes with real-time visual validation (no auto-correcting)
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        let fieldErr = "";
        if (name === "email") {
            if (containsExecutableMarkup(value)) {
                fieldErr = "Email cannot contain script tags or HTML code";
            } else if (value.trim() && !/\S+@\S+\.\S+/.test(value)) {
                fieldErr = "Please enter a valid email address";
            }
        } else if (name === "password") {
            if (containsExecutableMarkup(value)) {
                fieldErr = "Password cannot contain script tags or HTML code";
            }
        }

        setErrors(prev => ({
            ...prev,
            [name]: fieldErr
        }));
    };

    // Handle checkbox change
    const handleRememberMeChange = (e) => {
        setRememberMe(e.target.checked);
    };

    // Validate form: only validate that email and password are non-empty strings on login
    const validateForm = () => {
        const newErrors = {};

        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else if (containsExecutableMarkup(formData.email)) {
            newErrors.email = "Email cannot contain script tags or HTML code";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = "Email is invalid";
        }

        if (!formData.password) {
            newErrors.password = "Password is required";
        } else if (containsExecutableMarkup(formData.password)) {
            newErrors.password = "Password cannot contain script tags or HTML code";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleLoginFailure = (error) => {
        const errorMsg = typeof error === "string" ? error : error?.msg || error?.message || "";
        const errorData = typeof error?.data === "string" ? error.data : "";
        const combinedText = `${errorMsg} ${errorData}`.toLowerCase();

        // Detect account locked conditions from backend messages
        const isLocked =
            combinedText.includes("locked") ||
            combinedText.includes("too many failed") ||
            combinedText.includes("account temporarily locked") ||
            combinedText.includes("maximum failed attempts");

        if (isLocked) {
            setIsAccountLocked(true);
            setErrors({
                general: errorData || errorMsg || "Account is temporarily locked. Please try again in 15 minutes."
            });
        } else {
            setIsAccountLocked(false);
            const warningMsg = errorData || errorMsg || "Invalid email or password.";
            setErrors({
                general: warningMsg
            });
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();

        if (isAccountLocked && lockoutSeconds > 0) {
            return;
        }

        // Anti-tamper check: ensure no errors or markup can bypass validation
        if (
            containsExecutableMarkup(formData.email) ||
            containsExecutableMarkup(formData.password) ||
            !validateForm()
        ) {
            return;
        }

        setLoader(true);

        try {
            const response = await authService.login(
                deepSanitizePayload(formData.email.trim()),
                formData.password
            );

            if (response.success) {
                setLockoutSeconds(0);
                setIsAccountLocked(false);
                // Setup token refresh
                authService.setupTokenRefresh();

                // Role-aware primary landing screen
                const currentUser = authService.getUser();
                const roleId = Number(currentUser?.role_id);

                if (roleId === 7) {
                    // Pharmacist -> Pharmacy Prescriptions & Dispense Queue
                    navigate("/pharmacy");
                } else if (roleId === 8) {
                    // Billing -> Billing & Accounts
                    navigate("/billing");
                } else if (roleId === 6) {
                    // Lab Technician -> Lab Reports
                    navigate("/create-lab-report");
                } else if (roleId === 1 || roleId === 2 || roleId === 11) {
                    // Super Admin, Hospital Admin & Auditor -> Dashboard
                    navigate("/dashboard");
                } else {
                    // Doctor, Nurse, Receptionist, Staff, HOD -> OPD Queue / Patients
                    navigate("/patients");
                }
            } else {
                handleLoginFailure(response);
            }
        } catch (error) {
            console.error("Login error:", error);
            handleLoginFailure(error);
        } finally {
            setLoader(false);
        }
    };



    return (
        <>
            {loader && <Loader />}

            <div className="login-page">
                <div className="left-section">
                    <div className="custome-coursole">
                        <Swiper pagination={true} modules={[Pagination]} className="mySwiper">
                            <SwiperSlide>
                                <div className="coursol-details">
                                    <div className="coursole-img">
                                        <img src={ImgPhone} alt="Phone" />
                                    </div>
                                    <div className="coursole-para">
                                        <h5>Demo text</h5>
                                        <p>Lorem ipsum dolor sit amet consectetur. Mi cursus senectus id <br />posuere donec turpis mauris pharetra urna. Gravida faucibus  </p>
                                    </div>
                                </div>
                            </SwiperSlide>
                            <SwiperSlide>
                                <div className="coursol-details">
                                    <div className="coursole-img">
                                        <img src={ImgPhone} alt="Phone" />
                                    </div>
                                    <div className="coursole-para">
                                        <h5>Demo text</h5>
                                        <p>Lorem ipsum dolor sit amet consectetur. Mi cursus senectus id posuere donec turpis mauris pharetra urna. Gravida faucibus  </p>
                                    </div>
                                </div>
                            </SwiperSlide>
                            <SwiperSlide>
                                <div className="coursol-details">
                                    <div className="coursole-img">
                                        <img src={ImgPhone} alt="Phone" />
                                    </div>
                                    <div className="coursole-para">
                                        <h5>Demo text</h5>
                                        <p>Lorem ipsum dolor sit amet consectetur. Mi cursus senectus id posuere donec turpis mauris pharetra urna. Gravida faucibus  </p>
                                    </div>
                                </div>
                            </SwiperSlide>
                        </Swiper>
                    </div>
                    <div className="white-logo">
                        <img src={WhiteLogo} alt="Logo" />
                    </div>
                </div>
                <div className="right-section">
                    <div className="logo-section-login">
                        <img src={Logo} alt="Logo" />
                    </div>
                    <div className="login-section">
                        <h4>Login into your account</h4>
                        <form onSubmit={handleLogin}>
                            <div className="loginFeilds">
                                {isAccountLocked ? (
                                    <div
                                        className="account-locked-alert alert alert-danger p-3 mb-3 text-start"
                                        role="alert"
                                        style={{
                                            borderRadius: "8px",
                                            border: "1px solid #f5c2c7",
                                            backgroundColor: "#f8d7da",
                                            color: "#842029"
                                        }}
                                    >
                                        <div className="d-flex align-items-start gap-2">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#842029" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }}>
                                                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
                                                <line x1="12" y1="9" x2="12" y2="13"></line>
                                                <line x1="12" y1="17" x2="12.01" y2="17"></line>
                                            </svg>
                                            <div>
                                                <div className="fw-bold mb-1" style={{ fontSize: "14px", color: "#842029" }}>
                                                    Account Temporarily Locked
                                                </div>
                                                <div className="mb-2" style={{ fontSize: "13px", lineHeight: 1.4 }}>
                                                    Your account is locked for {lockoutSeconds > 0 ? `${Math.ceil(lockoutSeconds / 60)} minute(s)` : "15 minutes"} due to multiple failed login attempts.
                                                    {lockoutSeconds > 0 && (
                                                        <span className="ms-1 fw-bold">
                                                            ({Math.floor(lockoutSeconds / 60)}m {String(lockoutSeconds % 60).padStart(2, '0')}s remaining)
                                                        </span>
                                                    )}
                                                </div>
                                                <div style={{ fontSize: "13px" }}>
                                                    Please wait or{" "}
                                                    <Link
                                                        to="/forgot-password"
                                                        className="fw-bold text-decoration-underline"
                                                        style={{ color: "#0d6efd" }}
                                                    >
                                                        Reset Your Password
                                                    </Link>{" "}
                                                    to unlock immediately.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : errors.general ? (
                                    <div
                                        className="error-message alert alert-warning py-2 px-3 mb-3 text-start"
                                        role="alert"
                                        style={{
                                            borderRadius: "6px",
                                            fontSize: "13px",
                                            border: "1px solid #ffeeba",
                                            backgroundColor: "#fff3cd",
                                            color: "#856404"
                                        }}
                                    >
                                        {errors.general}
                                    </div>
                                ) : null}
                                <div className="feilds">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        autoComplete="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="Enter your email"
                                        className={errors.email ? 'error' : ''}
                                        required />
                                    {errors.email && (
                                        <small>
                                            {errors.email}
                                        </small>
                                    )}
                                </div>
                                <div className="feilds">
                                    <label>Password</label>
                                    <input
                                        type="password"
                                        name="password"
                                        autoComplete="current-password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        placeholder="Enter your password"
                                        className={errors.password ? 'error' : ''}
                                        required />
                                    {errors.password && (
                                        <small>
                                            {errors.password}
                                        </small>
                                    )}
                                </div>
                                <div className="feilds remember-forgot">
                                    <div className="rememberMe form-check checkBoxCustomeSection">
                                        <label className="checkBoxCustome">Remember me
                                            <input
                                                type="checkbox"
                                                checked={rememberMe}
                                                onChange={handleRememberMeChange}
                                            />
                                            <span className="checkmark" />
                                        </label>
                                    </div>
                                    <div
                                        className="forgot-text"
                                        onClick={() => navigate("/forgot-password")}
                                        onKeyDown={(e) => e.key === "Enter" && navigate("/forgot-password")}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        Forgot Password?
                                    </div>
                                </div>
                                <div className="feilds">
                                    <button
                                        type="submit"
                                        className="customeBtn successBtn w-100 px-3 py-2"
                                        disabled={
                                            loader ||
                                            (isAccountLocked && lockoutSeconds > 0) ||
                                            Boolean(errors.email) ||
                                            Boolean(errors.password) ||
                                            containsExecutableMarkup(formData.email) ||
                                            containsExecutableMarkup(formData.password)
                                        }
                                        style={((isAccountLocked && lockoutSeconds > 0) || Boolean(errors.email) || Boolean(errors.password)) ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                                    >
                                        {loader ? "Logging in..." : (isAccountLocked && lockoutSeconds > 0) ? `Account Locked (${Math.floor(lockoutSeconds / 60)}m ${String(lockoutSeconds % 60).padStart(2, '0')}s)` : "Log in"}
                                    </button>
                                </div>
                                <div className="dontHaveAccRequestAdmin">
                                    <p>Don't have account?</p>
                                    <div className="requestAdmin">Request to Admin</div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Login;